import React, { useEffect, useState } from "react";
import { getListings, getListingsGeometry } from "@/services/api";
import { useInfiniteQuery, useQuery } from "react-query";
import Image from "next/image";
import PropertiesList from "./components/PropertiesList";
import { GetListings } from "@/services/apiTypes";
import { outcodes } from "@/utils/outcodes"; // Importing the outcodes list
import PinHollow from "@/public/icons/pin-orange-hollow.svg";
import { useRouter } from "next/router";
import { formatSearchTerm } from "@/utils/formatText";
import AreaMap from "@/components/properties/AreaMap";
import LocationFilterBar from "@/components/properties/ListingFilterBar";
import { ParkingType } from "@/components/properties/ListingFilterBar/ListingFilterBar";
import { Dropdown } from "@/atoms/Dropdown/Dropdown";
import { routes } from "@/constants/routes/routes";
import { getDevelopementsDropdowns } from "@/services/cms/api";
import { useGetOneDevelopmentQuery } from "@/services/cms/queries";
import { LoaderIcon } from "lucide-react";

const PropertySearch = (props: {
  searchTerm?: string;
  hidePropertiesIfNoProperties?: boolean;
  status?: string;
}): JSX.Element => {
  const { data: dropdownData } = useQuery("developments-dropdowns", getDevelopementsDropdowns, {
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const { data: oneDevelopment, isFetched: isDevelopmentFetched } = useGetOneDevelopmentQuery();

  const initalLimit = oneDevelopment ? 13 : 14;
  const limit = 15;
  const { searchTerm, hidePropertiesIfNoProperties = false, status } = props;
  const router = useRouter();
  const [listingType, setListingType] = useState<"list" | "map">("list");
  const [sortField, setSortField] = useState<string | null>(() =>
    typeof router.query.sort === "string" ? router.query.sort : null,
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(() => {
    const d = router.query.dir;
    return d === "asc" || d === "desc" ? d : null;
  });
  const [bedrooms, setBedrooms] = useState<number | null>(() => {
    const b = Number(router.query.beds);
    return b >= 1 && b <= 3 ? b : null;
  });
  const [parkingSpace, setParkingSpace] = useState<ParkingType>(() => {
    const p = router.query.parking;
    return p === "Yes" || p === "No" ? p : null;
  });
  const [bathrooms, setBathrooms] = useState<number | null>(() => {
    const b = Number(router.query.baths);
    return b >= 1 && b <= 3 ? b : null;
  });
  const [availability, setAvailability] = useState<"available" | "unavailable" | null>(() => {
    const a = router.query.availability ?? status;
    return a === "available" || a === "unavailable" ? a : null;
  });
  const [openSortAndFilter, setOpenSortAndFilter] = useState<"sort" | "filter" | null>(null);
  const [postcodeCordinates, setPostcodeCoordinates] = useState<
    { lat: number; lng: number }[] | []
  >([]);
  const [isMapDataLoading, setIsMapDataLoading] = useState<boolean>(false);
  const [listingsLatLong, setListingsLatLong] = useState<
    { id: number; location_lat: number; location_long: number }[] | []
  >([]);

  const formatPostcode = (postcode: string) => {
    if (outcodes.includes(postcode.toUpperCase())) {
      return postcode.toUpperCase();
    }
    return postcode.toLowerCase().replace(/(^|\s)\w/g, (match) => match.toUpperCase());
  };

  const {
    data: listingsData,
    fetchNextPage,
    hasNextPage,
    isFetching: isLoadingListing,
  } = useInfiniteQuery(
    [
      "listings",
      searchTerm,
      bedrooms,
      bathrooms,
      availability,
      parkingSpace,
      sortField,
      sortDirection,
    ],
    ({
      pageParam = {
        limit: initalLimit,
        offset: 0,
        search_term: searchTerm,
        bedrooms,
        bathrooms,
        availability,
        parkingSpace,
        sortDirection,
        sortField,
      } as GetListings,
    }) => {
      return getListings(pageParam);
    },
    {
      cacheTime: 0,
      enabled: isDevelopmentFetched,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset + lastPage.limit >= lastPage.total) return false;

        return {
          limit: limit,
          offset: lastPage.offset + lastPage.limit,
          search_term: searchTerm,
          bedrooms,
          bathrooms,
          availability,
          parkingSpace,
          sortDirection,
          sortField,
        };
      },
    },
  );
  const fetchMapData = async () => {
    let postcode = "";
    if (searchTerm) {
      switch (searchTerm) {
        case "hayes":
          postcode = "UB3";
          break;
        case "harrow":
          postcode = "HA1";
          break;
        case "southall":
          postcode = "UB1";
          break;
        case "wandsworth":
          postcode = "SW17";
          break;
        default:
          postcode = formatPostcode(searchTerm);
      }
    }
    setIsMapDataLoading(true);

    try {
      const res = await getListingsGeometry({
        districtCode: postcode ? formatPostcode(postcode) : undefined,
        bedrooms,
        bathrooms,
        availability: "unreserved", // Only show unreserved properties on the map
      });
      setPostcodeCoordinates(res.polygonCoordinates);
      setListingsLatLong(res.result);
    } catch (error) {
      console.error("Error fetching map data:", error);
    }
    setIsMapDataLoading(false);
  };

  useEffect(() => {
    if (listingType === "map") {
      fetchMapData();
    }
  }, [listingType, searchTerm, bathrooms, bedrooms, availability]);

  useEffect(() => {
    setListingType("list");
  }, [searchTerm]);

  useEffect(() => {
    if (!router.isReady) return;
    const query: Record<string, string> = {};
    if (bedrooms !== null) query.beds = String(bedrooms);
    if (bathrooms !== null) query.baths = String(bathrooms);
    if (parkingSpace !== null) query.parking = parkingSpace;
    if (availability !== null) query.availability = availability;
    if (sortField !== null) query.sort = sortField;
    if (sortDirection !== null) query.dir = sortDirection;
    router.replace({ pathname: router.pathname, query: { ...router.query, ...query } }, undefined, {
      shallow: true,
    });
  }, [bedrooms, bathrooms, parkingSpace, availability, sortField, sortDirection]);

  const showNoPropertiesFound =
    isLoadingListing === false &&
    isMapDataLoading === false &&
    ((listingType === "list" &&
      (listingsData?.pages.flatMap((page) => page.result) || []).length === 0) ||
      (listingType === "map" && listingsLatLong.length === 0));

  const onSelectLocation = (locationId: string | null) => {
    if (locationId === null) {
      router.push({ pathname: routes.searchHomes });
    } else {
      router.push({
        pathname: routes.properties[locationId as keyof typeof routes.properties],
      });
    }
  };

  return (
    <div className="relative px-5 mt-10 mb-10 xl:mb-14 xl:mt-20">
      <div className="relative text-left xl:text-center md:mb-14 ">
        <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
          <Image
            src="/shared/shapes.svg"
            alt="shapes doodles"
            objectFit="contain"
            layout="fill"
            priority
          />
        </div>

        <h1 className="xl:w-2/3 mx-auto mb-10 font-bold text-3xl xs:text-[44px] md:text-[56px] xl:text-[76px] xs:leading-[46px] md:leading-[59px] xl:leading-[80px] text-center">
          {searchTerm === "wandsworth"
            ? `Buy a home in Wandsworth with Keyzy`
            : searchTerm
            ? `Rent-to-own a home in  ${formatSearchTerm(searchTerm)} with Keyzy`
            : "Keyzy Homes"}
        </h1>
        <p className="xl:w-3/4 mx-auto mb-10 font-medium text-xl text-center">
          {searchTerm === "wandsworth"
            ? `Rent-to-own a beautifully designed home in Springfield Village, Wandsworth (SW17) — a premium new development combining modern living with green open spaces and a clear path to ownership`
            : searchTerm
            ? `Looking for rent to own properties in ${formatSearchTerm(
                searchTerm,
              )}? Keyzy Homes offers zero deposit rent to buy options to help first-time buyers step onto the property ladder. Our 1, 2 & 3-bed apartments for rent will help you get from renting to owning without an upfront deposit.`
            : `Discover flexible homeownership with rent to buy homes in London. Browse 1, 2 & 3 bed apartments with zero deposit options, perfect for first-time buyers looking for affordable rent to own properties.`}
        </p>
        {!hidePropertiesIfNoProperties && (
          <LocationFilterBar
            listingType={listingType}
            setListingType={setListingType}
            sortField={sortField}
            setSortField={setSortField}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            openSortAndFilter={openSortAndFilter}
            setOpenSortAndFilter={setOpenSortAndFilter}
            bedrooms={bedrooms}
            setBedrooms={setBedrooms}
            bathrooms={bathrooms}
            setBathrooms={setBathrooms}
            availability={availability}
            setAvailability={setAvailability}
            searchTerm={searchTerm}
            parkingSpace={parkingSpace}
            setParkingSpace={setParkingSpace}
          />
        )}
        {/* TODO: Remove this when we have more properties */}
        {/* {searchTerm && (<div className="w-full mx-auto mt-6 max-w-[1280px] px-5 lg:px-0 mb-2 flex flex-row ">
          <Link href={routes.searchHomes}>
            <a className="flex flex-row align-middle items-center text-landing-orange font-bold">
              <RightArrow className="md:mr-3 mr-1  rotate-180" />
              Back to all homes
            </a>
          </Link>
        </div>)} */}

        <div className="absolute hidden -right-10 -top-5 w-96 h-36 xl:inline-block">
          <Image
            src="/shared/shapes.svg"
            alt="shapes doodles 2"
            objectFit="contain"
            layout="fill"
            priority
          />
        </div>
      </div>
      {/* Temporarily remove until we have more properties */}
      {/* <SearchComponent
        searchSuggestions={listingSearchSuggestion || []}
        searchTerm={searchTerm}
        setQuery={setQuery as any}
        query={query as string}
        isLoadingListing={isLoadingListing || isLoadingSuggestions}
        searchListings={searchListings}
      /> */}

      {hidePropertiesIfNoProperties ? (
        <></>
      ) : showNoPropertiesFound ? (
        <>
          <section className="flex flex-col items-center justify-center w-auto mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:px-[1.5rem]">
            <p className="xl:w-5/6 mx-auto mb-4 font-bold text-2xl text-center">
              Sorry, we don’t have anything that matches your criteria. Try removing filters or
              looking at somewhere else...
            </p>
            {Number(listingsData?.pages[0]?.total_listings_without_filters) > 0 && (
              <p className="xl:w-5/6 mx-auto mb-4 font-bold text-2xl text-center">
                {`${
                  listingsData?.pages[0]?.total_listings_without_filters || 0
                } homes matching less of your criteria`}
              </p>
            )}
          </section>
        </>
      ) : listingType === "list" ? (
        <PropertiesList
          isLoadingListing={isLoadingListing}
          listings={listingsData?.pages.flatMap((page) => page.result) || []}
          hasNextPage={hasNextPage || false}
          searchTerm={searchTerm}
          handleLoadMoreProperties={() => fetchNextPage()}
          searchRadius={0}
          totalListings={listingsData?.pages[0]?.total_listings_without_filters || 0}
          oneDevelopment={oneDevelopment}
        />
      ) : (
        <AreaMap
          postcodeCoordinates={postcodeCordinates}
          listingsLatLong={listingsLatLong}
          isMapDataLoading={isMapDataLoading}
        />
      )}

      {searchTerm && (
        <div className="flex  gap-4 mt-4 max-w-[470px] mx-auto relative flex-col">
          <p className="text-center text-landing-blue">Find your ideal home in</p>
          {dropdownData ? (
            <Dropdown
              LeftIcon={<PinHollow className=" fill-landing-orange" />}
              title="Select a Keyzy Development"
              data={[
                ...dropdownData,
                { key: null, heading: "All Keyzy homes", subHeading: "London" },
              ]}
              selectedData={searchTerm}
              setSelectedData={onSelectLocation}
            />
          ) : (
            <div className="w-full flex items-center justify-center border border-landing-orange rounded-full h-[66px]">
              <LoaderIcon className="animate-spin" stroke="#f39237" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertySearch;
